import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      setTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken, isAuthenticated: true });
      },

      setToken: (accessToken) => {
        set({ accessToken, isAuthenticated: true });
      },

      setUser: (user) => {
        set({ user });
      },

      login: async (email, password, rememberMe = false) => {
        set({ isLoading: true, error: null });
        try {
          const response = await axios.post(`${API_URL}/api/auth/login`, { email, password, remember_me: rememberMe });
          if (response.data.success) {
            // Check if 2FA is required
            if (response.data.data?.requires_2fa) {
              set({ isLoading: false });
              return { success: false, requires_2fa: true, user_id: response.data.data.user_id, user_name: response.data.data.user_name, remember_me: response.data.data.remember_me };
            }
            const { access_token, refresh_token } = response.data.data;
            set({ 
              accessToken: access_token, 
              refreshToken: refresh_token,
              isAuthenticated: true,
              isLoading: false 
            });
            await get().fetchUser();
            return { success: true };
          }
        } catch (error) {
          const message = error.response?.data?.detail || 'Login failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      login2fa: async (userId, code, isBackup = false, rememberMe = false) => {
        set({ isLoading: true, error: null });
        try {
          const response = await axios.post(`${API_URL}/api/auth/login/2fa`, { user_id: userId, code, is_backup: isBackup, remember_me: rememberMe });
          if (response.data.success) {
            const { access_token, refresh_token } = response.data.data;
            set({
              accessToken: access_token,
              refreshToken: refresh_token,
              isAuthenticated: true,
              isLoading: false
            });
            await get().fetchUser();
            return { success: true };
          }
        } catch (error) {
          const message = error.response?.data?.detail || 'Gecersiz 2FA kodu';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await axios.post(`${API_URL}/api/auth/register`, data);
          if (response.data.success) {
            set({ isLoading: false });
            return { success: true, message: response.data.message };
          }
        } catch (error) {
          const message = error.response?.data?.detail || 'Registration failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      fetchUser: async () => {
        const { accessToken } = get();
        if (!accessToken) return;
        
        try {
          const response = await axios.get(`${API_URL}/api/me`, {
            headers: { Authorization: `Bearer ${accessToken}` }
          });
          if (response.data.success) {
            set({ user: response.data.data });
          }
        } catch (error) {
          if (error.response?.status === 401) {
            get().logout();
          }
        }
      },

      updateLanguage: async (language) => {
        const { accessToken } = get();
        try {
          await axios.put(`${API_URL}/api/me/language`, { language }, {
            headers: { Authorization: `Bearer ${accessToken}` }
          });
          set((state) => ({ user: { ...state.user, language } }));
        } catch (error) {
          console.error('Failed to update language', error);
        }
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return false;
        
        try {
          const response = await axios.post(`${API_URL}/api/auth/refresh`, {
            refresh_token: refreshToken
          });
          if (response.data.success) {
            const { access_token, refresh_token } = response.data.data;
            set({ accessToken: access_token, refreshToken: refresh_token });
            return true;
          }
        } catch (error) {
          get().logout();
          return false;
        }
      },

      clearTokens: () => {
        set({ 
          accessToken: null, 
          refreshToken: null, 
          isAuthenticated: false 
        });
      },

      logout: () => {
        set({ 
          user: null, 
          accessToken: null, 
          refreshToken: null, 
          isAuthenticated: false 
        });
      },

      getAuthHeaders: () => {
        const { accessToken } = get();
        return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
      }
    }),
    {
      name: 'hr-auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        user: state.user
      })
    }
  )
);

// Cross-tab sync: when auth tokens are cleared/changed in another tab, sync here
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'hr-auth-storage') {
      try {
        const data = e.newValue ? JSON.parse(e.newValue) : null;
        const state = data?.state;
        if (state) {
          useAuthStore.setState({
            accessToken: state.accessToken || null,
            refreshToken: state.refreshToken || null,
            isAuthenticated: !!state.isAuthenticated,
            user: state.user || null,
          });
        } else {
          useAuthStore.setState({
            accessToken: null, refreshToken: null,
            isAuthenticated: false, user: null,
          });
        }
      } catch {}
    }
  });
}

// Axios interceptor for auth
const createAuthAxios = () => {
  const instance = axios.create({
    baseURL: `${API_URL}/api`
  });

  instance.interceptors.request.use((config) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        const refreshed = await useAuthStore.getState().refreshAccessToken();
        if (refreshed) {
          error.config.headers.Authorization = `Bearer ${useAuthStore.getState().accessToken}`;
          return axios(error.config);
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export const api = createAuthAxios();
